import { DataTableProps } from "components/dashboard/DataTable";
import { ptBR } from "date-fns/locale";

import { DashboardBillingValues } from "libLegacy/api/models";

const placeholderBrl = "R$ -";
const placeholderKwh = "- kwh";

export const parseEconomy = (
  monthData?: DashboardBillingValues,
  isProsumer?: boolean
): DataTableProps["data"] => {
  if (isProsumer) {
    return [
      {
        key: "Quanto pagaria sem sistema",
        value: monthData?.costWithoutInjectedEnergy
          ? (+monthData.costWithoutInjectedEnergy).toLocaleString(ptBR.code, {
              style: "currency",
              currency: "BRL",
            })
          : placeholderBrl,
        tooltipText:
          "Esse valor se refere à quantia que você iria pagar caso não tivesse sistema solar.",
      },
      {
        key: "Valor total da fatura",
        value: monthData?.finalBillingAmount
          ? (+monthData.finalBillingAmount).toLocaleString(ptBR.code, {
              style: "currency",
              currency: "BRL",
            })
          : placeholderBrl,
        tooltipText:
          "Esse valor se refere à quantia que você irá pagar esse mês e o consumo mínimo cobrado pela concessionária.",
      },
      {
        key: "Economia",
        value: monthData?.savingsTotal
          ? (+monthData.savingsTotal).toLocaleString(ptBR.code, {
              style: "currency",
              currency: "BRL",
            })
          : placeholderBrl,
        variant: "positive",
      },
    ];
  } else {
    return [
      {
        key: "Quanto pagaria sem energia verde",
        value: monthData?.totalWithoutGenerationBrl
          ? (+monthData.totalWithoutGenerationBrl).toLocaleString(ptBR.code, {
              style: "currency",
              currency: "BRL",
            })
          : placeholderBrl,
      },
      {
        key: "Valor a pagar total",
        value: monthData?.finalBillingAmount
          ? (+monthData.finalBillingAmount).toLocaleString(ptBR.code, {
              style: "currency",
              currency: "BRL",
            })
          : placeholderBrl,
      },
      {
        key: "Distribuidora de energia",
        value: monthData?.invoiceChargeBrl
          ? (+monthData.invoiceChargeBrl).toLocaleString(ptBR.code, {
              style: "currency",
              currency: "BRL",
            })
          : placeholderBrl,
        variant: "indented",
      },
      {
        key: "Energia verde",
        value: monthData?.generationChargeBrl
          ? (+monthData.generationChargeBrl).toLocaleString(ptBR.code, {
              style: "currency",
              currency: "BRL",
            })
          : placeholderBrl,
        variant: "indented",
      },
      {
        key: "Economia",
        value: monthData?.generationEconomyBrl
          ? (+monthData.generationEconomyBrl).toLocaleString(ptBR.code, {
              style: "currency",
              currency: "BRL",
            })
          : placeholderBrl,
        variant: "positive",
      },
    ];
  }
};

export const parseConsumption = (
  isProsumer: boolean,
  monthData?: DashboardBillingValues
): DataTableProps["data"] => {
  const registeredUsage = monthData?.registeredUsage
    ? `${(+monthData?.registeredUsage).toLocaleString(ptBR.code, {
        maximumFractionDigits: 0,
      })} kwh`
    : placeholderKwh;
  const compensatedEnergy = monthData?.compensatedEnergy
    ? `${(+monthData?.compensatedEnergy).toLocaleString(ptBR.code, {
        maximumFractionDigits: 0,
      })} kwh`
    : placeholderKwh;

  const data = [
    {
      key: "Seu consumo deste mês",
      value: registeredUsage,
      tooltipText:
        "Esse valor se refere ao seu consumo do mês em kWh e o consumo mínimo cobrado pela concessionária.",
    },
    {
      key: "Energia compensada",
      value: compensatedEnergy || "",
      tooltipText:
        "Esse valor se refere à energia à diferença entre o valor em kWh de energia que você consumiu e que você injetou, que sua concessionária de energia compensou.",
    },
  ];

  if (isProsumer) {
    // zero is a valid value because the user should have this data if monitoring is enabled
    const generatedEnergyKwh = `${(+(
      monthData?.generatedEnergyKwh ?? 0
    )).toLocaleString(ptBR.code, {
      maximumFractionDigits: 0,
    })} kwh`;
    const injectedEnergyKwh = `${(+(
      monthData?.injectedEnergyKwh ?? 0
    )).toLocaleString(ptBR.code, {
      maximumFractionDigits: 0,
    })} kwh`;

    data.splice(
      1,
      0,
      {
        key: "Energia gerada pelo seu sistema",
        value: generatedEnergyKwh || "",
        tooltipText:
          "Esse valor se refere ao valor em kWh de energia gerada pelo seu sistema solar.",
      },
      {
        key: "Energia injetada",
        value: injectedEnergyKwh || "",
        tooltipText:
          "Esse valor se refere a energia gerada pelo seu sistema solar que foi injetada na rede da concessionária. A diferença entre energia gerada e a energia injetada equivale ao seu autoconsumo, ou seja, o que você gerou e consumiui simultaneamente.",
      }
    );
  }

  return data;
};

export const parseCredits = (
  monthData?: DashboardBillingValues
): DataTableProps["data"] => {
  const accumulatedCredit = `${(+(
    monthData?.accumulatedCredit || 0
  )).toLocaleString(ptBR.code, {
    maximumFractionDigits: 0,
  })} kwh`;
  const accMonthly = +(monthData?.monthlyAcculumatedCreditKwh || 0);

  return [
    {
      key: "Saldo de créditos ",
      value: accumulatedCredit,
      tooltipText:
        "Esse valor se refere a quantidade de créditos (kWh) acumulados ao longo do tempo, representando a diferença entre a energia gerada pelo sistema solar e a energia consumida da rede elétrica. Este saldo pode ser utilizado em até 60 meses.",
    },
    {
      key:
        accMonthly < 0
          ? "Créditos utilizados esse mês"
          : "Créditos acumulados esse mês",
      value: `${accMonthly.toLocaleString(ptBR.code, {
        maximumFractionDigits: 0,
      })} kwh`,
      tooltipText: `Esse valor se refere aos créditos ${
        accMonthly < 0 ? "deduzidos" : "adicionados"
      } ao seu saldo neste mês.`,
    },
  ];
};

export const parseGreenEnergy = (
  monthData?: DashboardBillingValues
): DataTableProps["data"] => {
  const {
    totalCompensationCostBrl,
    contractedDiscount,
    generationEconomyBrl,
    generationChargeBrl,
  } = monthData || {};

  return [
    {
      key: "Quanto pagaria para distribuidora",
      value: totalCompensationCostBrl
        ? totalCompensationCostBrl.toLocaleString(ptBR.code, {
            style: "currency",
            currency: "BRL",
          })
        : placeholderBrl,
      tooltipText:
        "Valor bruto que seria pago para a distribuidora pela quantidade de energia verde",
    },
    {
      key: "Desconto contratado",
      value: contractedDiscount
        ? (contractedDiscount / 100).toLocaleString(ptBR.code, {
            style: "percent",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : " - %",
      tooltipText:
        "É aplicado sobre o que você deixou de pagar para a distribuidora de energia",
    },
    {
      key: "Economia",
      value: generationEconomyBrl
        ? generationEconomyBrl.toLocaleString(ptBR.code, {
            style: "currency",
            currency: "BRL",
          })
        : placeholderBrl,
      tooltipText:
        "Resultado da multiplicação do quanto você pagaria para a distribuidora pelo desconto contratado",
    },
    {
      key: "Valor da energia verde",
      value: generationChargeBrl
        ? generationChargeBrl.toLocaleString(ptBR.code, {
            style: "currency",
            currency: "BRL",
          })
        : placeholderBrl,
      tooltipText:
        "Resultado da subtração de quanto pagaria para a distribuidora e a economia",
    },
  ];
};
